import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type TrackBehavior,
  type TrackNode,
  type Vec3,
} from '../schema/index.ts';
import { mergeBehavior, makeOrderedSection, PHASE_2_NODE_LIMIT } from './topology.ts';
import { buildValidationProfile, projectPlotBounds } from './validation.ts';
import { assignPieceGroup } from './piece-groups.ts';
import { assessModularFinishToStation } from './modular-finish.ts';

export type TrackOperationResult =
  | { readonly ok: true; readonly project: CoasterProject; readonly message: string }
  | { readonly ok: false; readonly code: string; readonly message: string };

export type FinishFailureCode =
  | 'already_closed'
  | 'needs_points'
  | 'node_limit'
  | 'too_far'
  | 'height_mismatch'
  | 'not_enough_room';

export interface FinishGuidance {
  readonly distanceM: number;
  readonly elevationDeltaM: number;
  readonly turnDegrees: number;
  readonly turnDirection: 'left' | 'right' | 'straight';
  readonly directJoinEligible: boolean;
  readonly generatedFinishEligible: boolean;
  readonly summary: string;
}

export type FinishAssessment =
  | {
      readonly ok: true;
      readonly project: CoasterProject;
      readonly mode: 'direct' | 'aligned' | 'generated' | 'modular';
      readonly message: string;
      readonly guidance: FinishGuidance;
      readonly addedSectionIds?: readonly string[];
      readonly routeSteps?: readonly {
        readonly shapeId: string;
        readonly label: string;
        readonly parameters: Record<string, unknown>;
        readonly trackType: string;
      }[];
      readonly routeLengthM?: number;
    }
  | {
      readonly ok: false;
      readonly code: FinishFailureCode;
      readonly message: string;
      readonly guidance: FinishGuidance;
    };

let fallbackId = 0;

export function newStableId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `${prefix}:${uuid}`;
  fallbackId += 1;
  return `${prefix}:${Date.now().toString(36)}:${fallbackId.toString(36)}`;
}

function touch(project: CoasterProject): CoasterProject {
  project.metadata.updatedAt = new Date().toISOString();
  project.validation = createUnvalidatedValidationSummary();
  return project;
}

function success(project: CoasterProject, message: string): TrackOperationResult {
  return { ok: true, project: touch(project), message };
}

function fail(code: string, message: string): TrackOperationResult {
  return { ok: false, code, message };
}

function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function subtract(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function scale(a: Vec3, amount: number): Vec3 {
  return { x: a.x * amount, y: a.y * amount, z: a.z * amount };
}

function length(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

function normalize(a: Vec3): Vec3 {
  const magnitude = Math.max(1e-8, length(a));
  return scale(a, 1 / magnitude);
}

function adjustBezierHandlesForNodeMoves(before: CoasterProject, after: CoasterProject): void {
  if (after.track.geometryKind !== 'modular_piecewise_bezier') return;
  const original = new Map(before.track.nodes.map((node) => [node.id, node.position]));
  const moved = new Map(
    after.track.nodes.flatMap((node) => {
      const prior = original.get(node.id);
      if (!prior) return [];
      const delta = subtract(node.position, prior);
      return length(delta) > 1e-9 ? [[node.id, delta] as const] : [];
    }),
  );
  for (const section of after.track.sections) {
    if (!section.geometry) continue;
    const fromDelta = moved.get(section.fromNodeId);
    const toDelta = moved.get(section.toNodeId);
    if (fromDelta) section.geometry.control1 = add(section.geometry.control1, fromDelta);
    if (toDelta) section.geometry.control2 = add(section.geometry.control2, toDelta);
  }
}

function midpoint(a: Vec3, b: Vec3): Vec3 {
  return scale(add(a, b), 0.5);
}

export function instantiateTemplate(
  template: CoasterProject,
  title = template.metadata.title,
): CoasterProject {
  const now = new Date().toISOString();
  const project = structuredClone(template);
  project.projectId = newStableId('project');
  project.revisionId = newStableId('revision');
  project.metadata = {
    ...project.metadata,
    title,
    createdAt: now,
    updatedAt: now,
    creator: { kind: 'local', id: null, displayName: 'Local creator' },
    tags: project.metadata.tags.filter((tag) => tag !== 'fixture'),
  };
  project.permissions = { visibility: 'private', allowReadOnlyShare: false, allowRemix: false };
  project.validation = createUnvalidatedValidationSummary();
  project.lineage = null;
  return project;
}

export function moveNodes(
  project: CoasterProject,
  nodeIds: readonly string[],
  delta: Vec3,
  snap = true,
): TrackOperationResult {
  if (!nodeIds.length) return fail('nothing_selected', 'Select at least one track point.');
  const selected = new Set(nodeIds);
  const next = structuredClone(project);
  const bounds = projectPlotBounds(project);
  for (const node of next.track.nodes) {
    if (!selected.has(node.id)) continue;
    const candidate = add(node.position, delta);
    node.position = {
      x: Math.max(bounds.minX, Math.min(bounds.maxX, snap ? Math.round(candidate.x) : candidate.x)),
      y: Math.max(
        bounds.minY,
        Math.min(bounds.maxY, snap ? Math.round(candidate.y * 2) / 2 : candidate.y),
      ),
      z: Math.max(bounds.minZ, Math.min(bounds.maxZ, snap ? Math.round(candidate.z) : candidate.z)),
    };
  }
  adjustBezierHandlesForNodeMoves(project, next);
  return success(next, nodeIds.length > 1 ? 'Track points moved.' : 'Track point moved.');
}

export function setNodeElevation(
  project: CoasterProject,
  nodeIds: readonly string[],
  elevation: number,
): TrackOperationResult {
  const selected = new Set(nodeIds);
  if (!selected.size) return fail('nothing_selected', 'Select a track point first.');
  const next = structuredClone(project);
  const primary = next.track.nodes.find((node) => selected.has(node.id));
  if (!primary) return fail('missing_node', 'That track point no longer exists.');
  const delta = Math.max(1, Math.min(65, elevation)) - primary.position.y;
  next.track.nodes.forEach((node) => {
    if (selected.has(node.id)) node.position.y = Math.max(1, Math.min(65, node.position.y + delta));
  });
  adjustBezierHandlesForNodeMoves(project, next);
  return success(next, 'Height updated.');
}

export function setNodeBank(
  project: CoasterProject,
  nodeIds: readonly string[],
  bankDegrees: number,
): TrackOperationResult {
  const selected = new Set(nodeIds);
  if (!selected.size) return fail('nothing_selected', 'Select a track point first.');
  const next = structuredClone(project);
  const clamped = Math.max(-90, Math.min(90, bankDegrees));
  next.track.nodes.forEach((node) => {
    if (selected.has(node.id)) node.bankDegrees = clamped;
  });
  return success(next, 'Bank updated.');
}

export function removeNodeRollWinding(
  project: CoasterProject,
  nodeIds: readonly string[],
): TrackOperationResult {
  const selected = new Set(nodeIds);
  if (!selected.size) return fail('nothing_selected', 'Select a track point first.');
  const next = structuredClone(project);
  let changed = false;
  next.track.nodes.forEach((node) => {
    if (!selected.has(node.id) || Math.abs(node.rollWindingDegrees) < 0.001) return;
    node.rollWindingDegrees = 0;
    changed = true;
  });
  if (!changed) return fail('no_winding', 'The selected track points have no inversion winding.');
  return success(next, 'Inversion winding removed from the selected track points.');
}

export function alignNodes(
  project: CoasterProject,
  nodeIds: readonly string[],
  axis: 'x' | 'y' | 'z',
): TrackOperationResult {
  if (nodeIds.length < 2) return fail('needs_selection', 'Select two or more track points.');
  const selected = new Set(nodeIds);
  const next = structuredClone(project);
  const values = next.track.nodes
    .filter((node) => selected.has(node.id))
    .map((node) => node.position[axis]);
  const target = values.reduce((sum, value) => sum + value, 0) / values.length;
  next.track.nodes.forEach((node) => {
    if (selected.has(node.id)) node.position[axis] = target;
  });
  adjustBezierHandlesForNodeMoves(project, next);
  return success(next, `Selected points aligned on ${axis.toUpperCase()}.`);
}

export function rotateNodes(
  project: CoasterProject,
  nodeIds: readonly string[],
  degrees: number,
): TrackOperationResult {
  if (nodeIds.length < 2)
    return fail('needs_selection', 'Select two or more track points to rotate.');
  const selected = new Set(nodeIds);
  const next = structuredClone(project);
  const nodes = next.track.nodes.filter((node) => selected.has(node.id));
  const center = nodes.reduce((sum, node) => add(sum, node.position), { x: 0, y: 0, z: 0 });
  const pivot = scale(center, 1 / nodes.length);
  const angle = (degrees * Math.PI) / 180;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  nodes.forEach((node) => {
    const x = node.position.x - pivot.x;
    const z = node.position.z - pivot.z;
    node.position.x = pivot.x + x * cosine - z * sine;
    node.position.z = pivot.z + x * sine + z * cosine;
  });
  adjustBezierHandlesForNodeMoves(project, next);
  return success(next, 'Selection rotated.');
}

export function insertNodeAtSection(
  project: CoasterProject,
  sectionId: string,
  sampledPosition?: Vec3,
): TrackOperationResult {
  if (project.track.nodes.length >= PHASE_2_NODE_LIMIT)
    return fail('node_limit', `This editor supports up to ${PHASE_2_NODE_LIMIT} track points.`);
  const sectionIndex = project.track.sections.findIndex((section) => section.id === sectionId);
  if (sectionIndex < 0) return fail('missing_section', 'Select a track section first.');
  const next = structuredClone(project);
  const section = next.track.sections[sectionIndex]!;
  const fromIndex = next.track.nodes.findIndex((node) => node.id === section.fromNodeId);
  const toIndex = next.track.nodes.findIndex((node) => node.id === section.toNodeId);
  const from = next.track.nodes[fromIndex];
  const to = next.track.nodes[toIndex];
  if (!from || !to) return fail('broken_section', 'That section is not connected correctly.');
  const sectionGeometry = 'geometry' in section ? section.geometry : undefined;
  const modularSplit =
    next.track.geometryKind === 'modular_piecewise_bezier' && sectionGeometry
      ? (() => {
          const q0 = midpoint(from.position, sectionGeometry.control1);
          const q1 = midpoint(sectionGeometry.control1, sectionGeometry.control2);
          const q2 = midpoint(sectionGeometry.control2, to.position);
          const r0 = midpoint(q0, q1);
          const r1 = midpoint(q1, q2);
          return { q0, q2, r0, r1, split: midpoint(r0, r1) };
        })()
      : null;
  const node: TrackNode = {
    id: newStableId('node'),
    position: modularSplit
      ? modularSplit.split
      : sampledPosition
        ? { ...sampledPosition }
        : midpoint(from.position, to.position),
    bankDegrees: (from.bankDegrees + to.bankDegrees) * 0.5,
    rollWindingDegrees: (from.rollWindingDegrees + to.rollWindingDegrees) * 0.5,
  };
  next.track.nodes.splice(sectionIndex + 1, 0, node);
  section.toNodeId = node.id;
  const secondSection = {
    ...makeOrderedSection(
      newStableId('section'),
      node.id,
      to.id,
      structuredClone(section.behavior),
    ),
    ...(modularSplit
      ? {
          geometry: {
            kind: 'cubic_bezier' as const,
            control1: modularSplit.r1,
            control2: modularSplit.q2,
          },
        }
      : {}),
  };
  if (modularSplit) {
    if (!('geometry' in section)) throw new Error('Expected modular section geometry.');
    section.geometry = {
      kind: 'cubic_bezier',
      control1: modularSplit.q0,
      control2: modularSplit.r0,
    };
  }
  next.track.sections.splice(sectionIndex + 1, 0, secondSection);
  return success(next, 'Track point inserted.');
}

export function deleteNode(project: CoasterProject, nodeId: string): TrackOperationResult {
  if (project.track.nodes.length <= 4)
    return fail('minimum_nodes', 'A rideable track needs at least four track points.');
  const nodeIndex = project.track.nodes.findIndex((node) => node.id === nodeId);
  if (nodeIndex < 0) return fail('missing_node', 'That track point no longer exists.');
  if (!project.track.closed && (nodeIndex === 0 || nodeIndex === project.track.nodes.length - 1))
    return fail('endpoint_delete', 'Extend or finish the open track before deleting an endpoint.');
  const incomingIndex =
    (nodeIndex - 1 + project.track.sections.length) % project.track.sections.length;
  const outgoingIndex = nodeIndex % project.track.sections.length;
  const incoming = project.track.sections[incomingIndex];
  const outgoing = project.track.sections[outgoingIndex];
  if (!incoming || !outgoing)
    return fail('broken_section', 'The neighboring sections are incomplete.');
  const behavior = mergeBehavior(incoming.behavior, outgoing.behavior);
  if (!behavior)
    return fail(
      'behavior_conflict',
      `This point joins ${incoming.behavior.kind} and ${outgoing.behavior.kind} sections. Change one section before deleting it.`,
    );
  const next = structuredClone(project);
  const nextIncoming = next.track.sections[incomingIndex]!;
  const nextOutgoing = next.track.sections[outgoingIndex]!;
  nextIncoming.toNodeId = nextOutgoing.toNodeId;
  nextIncoming.behavior = behavior;
  if (
    next.track.geometryKind === 'modular_piecewise_bezier' &&
    'geometry' in nextIncoming &&
    'geometry' in nextOutgoing &&
    nextIncoming.geometry &&
    nextOutgoing.geometry
  )
    nextIncoming.geometry.control2 = { ...nextOutgoing.geometry.control2 };
  next.track.nodes.splice(nodeIndex, 1);
  next.track.sections.splice(outgoingIndex, 1);
  const removedSectionId = outgoing.id;
  next.supports.suppressedSectionIds = next.supports.suppressedSectionIds.filter(
    (id) => id !== removedSectionId,
  );
  next.supports.manual = next.supports.manual
    .filter((support) => support.sectionId !== removedSectionId)
    .map((support) => ({ ...support }));
  next.highlights = next.highlights.map((highlight) =>
    highlight.sectionId === removedSectionId
      ? { ...highlight, sectionId: nextIncoming.id, distanceAlongSection: 0.75 }
      : highlight,
  );
  return success(next, 'Track point deleted.');
}

export function duplicateSection(project: CoasterProject, sectionId: string): TrackOperationResult {
  const sectionIndex = project.track.sections.findIndex((section) => section.id === sectionId);
  if (sectionIndex < 0) return fail('missing_section', 'Select a track section first.');
  const section = project.track.sections[sectionIndex]!;
  const from = project.track.nodes[sectionIndex]!;
  const to = project.track.nodes[(sectionIndex + 1) % project.track.nodes.length]!;
  const inserted = insertNodeAtSection(project, sectionId, midpoint(from.position, to.position));
  if (!inserted.ok) return inserted;
  const next = inserted.project;
  const newSection = next.track.sections[sectionIndex + 1];
  if (newSection) newSection.behavior = structuredClone(section.behavior);
  return success(next, 'Section duplicated.');
}

export function smoothNodes(
  project: CoasterProject,
  nodeIds: readonly string[],
): TrackOperationResult {
  if (nodeIds.length < 3)
    return fail('needs_selection', 'Select at least three neighboring points to smooth.');
  const selectedIndices = nodeIds
    .map((id) => project.track.nodes.findIndex((node) => node.id === id))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);
  if (
    selectedIndices.some((value, index) => index > 0 && value !== selectedIndices[index - 1]! + 1)
  )
    return fail('contiguous_selection', 'Smooth works on neighboring track points.');
  const next = structuredClone(project);
  const originals = project.track.nodes.map((node) => node.position);
  for (let listIndex = 1; listIndex < selectedIndices.length - 1; listIndex += 1) {
    const index = selectedIndices[listIndex]!;
    const previous = originals[(index - 1 + originals.length) % originals.length]!;
    const current = originals[index]!;
    const following = originals[(index + 1) % originals.length]!;
    const average = scale(add(previous, following), 0.5);
    next.track.nodes[index]!.position = {
      x: current.x * 0.45 + average.x * 0.55,
      y: Math.max(1, current.y * 0.65 + average.y * 0.35),
      z: current.z * 0.45 + average.z * 0.55,
    };
  }
  adjustBezierHandlesForNodeMoves(project, next);
  return success(next, 'Selection smoothed.');
}

export function autoBankNodes(
  project: CoasterProject,
  nodeIds: readonly string[],
): TrackOperationResult {
  if (!nodeIds.length) return fail('nothing_selected', 'Select track points to auto-bank.');
  const selected = new Set(nodeIds);
  const next = structuredClone(project);
  const startHeight = next.track.nodes[0]?.position.y ?? 4;
  next.track.nodes.forEach((node, index, nodes) => {
    if (!selected.has(node.id)) return;
    if (!next.track.closed && (index === 0 || index === nodes.length - 1)) return;
    const incoming =
      next.track.sections[(index - 1 + next.track.sections.length) % next.track.sections.length];
    const outgoingSection = next.track.sections[index % Math.max(1, next.track.sections.length)];
    if (
      [incoming?.behavior.kind, outgoingSection?.behavior.kind].some((kind) =>
        kind ? ['station', 'lift', 'brake', 'photo'].includes(kind) : false,
      ) ||
      Math.abs(node.rollWindingDegrees) > 0.001
    )
      return;
    const previous = nodes[(index - 1 + nodes.length) % nodes.length]!;
    const following = nodes[(index + 1) % nodes.length]!;
    const a = normalize(subtract(node.position, previous.position));
    const b = normalize(subtract(following.position, node.position));
    const signedTurn = a.x * b.z - a.z * b.x;
    const turnRadians = Math.acos(Math.max(-1, Math.min(1, a.x * b.x + a.z * b.z)));
    const averageSpan =
      (length(subtract(node.position, previous.position)) +
        length(subtract(following.position, node.position))) *
      0.5;
    const radius = averageSpan / Math.max(0.02, turnRadians);
    const estimatedSpeed = Math.sqrt(
      Math.max(4, 4 + 2 * 9.80665 * (startHeight - node.position.y)),
    );
    const target =
      (Math.sign(signedTurn) *
        (Math.atan((estimatedSpeed * estimatedSpeed) / (9.80665 * Math.max(5, radius))) * 180)) /
      Math.PI;
    node.bankDegrees = Math.max(-55, Math.min(55, node.bankDegrees * 0.25 + target * 0.75));
  });
  return success(next, 'Banking matched to the track shape.');
}

export function setSectionBehavior(
  project: CoasterProject,
  sectionId: string,
  behavior: TrackBehavior,
): TrackOperationResult {
  const next = structuredClone(project);
  const section = next.track.sections.find((candidate) => candidate.id === sectionId);
  if (!section) return fail('missing_section', 'Select a track section first.');
  section.behavior = structuredClone(behavior);
  return success(next, `${behavior.kind === 'brake' ? 'Brake' : behavior.kind} section updated.`);
}

function blockerCounts(project: CoasterProject): Record<string, number> {
  return buildValidationProfile(project, 0)
    .issues.filter((candidate) => candidate.severity === 'blocker')
    .reduce<Record<string, number>>((counts, candidate) => {
      counts[candidate.code] = (counts[candidate.code] ?? 0) + 1;
      return counts;
    }, {});
}

function addsNewBlocker(base: CoasterProject, candidate: CoasterProject): boolean {
  const baseline = blockerCounts(base);
  const next = blockerCounts(candidate);
  return Object.entries(next).some(
    ([code, count]) => code !== 'open_track' && count > (baseline[code] ?? 0),
  );
}

export function measureFinishToStation(project: CoasterProject): FinishGuidance {
  const nodes = project.track.nodes;
  if (nodes.length < 2)
    return {
      distanceM: 0,
      elevationDeltaM: 0,
      turnDegrees: 0,
      turnDirection: 'straight',
      directJoinEligible: false,
      generatedFinishEligible: false,
      summary: 'Place a station and build more track first.',
    };
  const first = nodes[0]!;
  const previous = nodes.at(-2)!;
  const last = nodes.at(-1)!;
  const gapVector = subtract(first.position, last.position);
  const distanceM = length(gapVector);
  const elevationDeltaM = first.position.y - last.position.y;
  const outgoing = normalize({
    x: last.position.x - previous.position.x,
    y: 0,
    z: last.position.z - previous.position.z,
  });
  const target = normalize({ x: gapVector.x, y: 0, z: gapVector.z });
  const cross = outgoing.x * target.z - outgoing.z * target.x;
  const directionDot = Math.max(-1, Math.min(1, outgoing.x * target.x + outgoing.z * target.z));
  const signedDegrees = (Math.atan2(cross, directionDot) * 180) / Math.PI;
  const turnDegrees = Math.abs(signedDegrees);
  const turnDirection =
    turnDegrees < 10 ? 'straight' : signedDegrees > 0 ? ('right' as const) : ('left' as const);
  const directJoinEligible =
    !project.track.closed && nodes.length >= 4 && distanceM <= 18 && Math.abs(elevationDeltaM) <= 5;
  const generatedFinishEligible =
    !project.track.closed &&
    nodes.length >= 4 &&
    distanceM <= (project.track.geometryKind === 'modular_piecewise_bezier' ? 96 : 85) &&
    Math.abs(elevationDeltaM) <=
      (project.track.geometryKind === 'modular_piecewise_bezier' ? 48 : 35) &&
    nodes.length + 4 <= PHASE_2_NODE_LIMIT;
  const directionText =
    turnDirection === 'straight'
      ? 'Station position: straight ahead from the track exit.'
      : `Station position: ${Math.round(turnDegrees)}° ${turnDirection} from the track exit.`;
  const summary = project.track.closed
    ? 'This track is already closed.'
    : nodes.length < 4
      ? 'Add more track before connecting to the station.'
      : distanceM > (project.track.geometryKind === 'modular_piecewise_bezier' ? 96 : 85)
        ? `The track exit is ${distanceM.toFixed(0)} m from the station. Move closer before connecting. ${directionText}`
        : Math.abs(elevationDeltaM) > 35
          ? `The track exit is ${Math.abs(elevationDeltaM).toFixed(0)} m too ${elevationDeltaM > 0 ? 'low' : 'high'}. Bring it closer to station height.`
          : `${distanceM.toFixed(0)} m from the station. ${directionText}`;
  return {
    distanceM,
    elevationDeltaM,
    turnDegrees,
    turnDirection,
    directJoinEligible,
    generatedFinishEligible,
    summary,
  };
}

export function connectEndpoints(project: CoasterProject): TrackOperationResult {
  const guidance = measureFinishToStation(project);
  if (project.track.closed) return fail('already_closed', 'This track is already closed.');
  if (project.track.nodes.length < 4)
    return fail('needs_points', 'Add more track before finishing to the station.');
  if (!guidance.directJoinEligible)
    return fail(
      'too_far',
      `Direct Join works within 18 m and 5 m of station height. ${guidance.summary}`,
    );
  const first = project.track.nodes[0]!;
  const last = project.track.nodes.at(-1)!;
  const next = structuredClone(project);
  next.track.closed = true;
  const closingSection = makeOrderedSection(newStableId('section'), last.id, first.id, {
    kind: 'brake',
    targetSpeedMps: 2.5,
    decelerationMps2: 8,
  });
  next.track.sections.push(closingSection);
  if (addsNewBlocker(project, next))
    return fail('not_enough_room', 'A direct join would create unsafe geometry here.');
  return success(
    assignPieceGroup(next, [closingSection.id], 'custom'),
    'Track finished to the station.',
  );
}

export function finishConnection(project: CoasterProject): TrackOperationResult {
  if (project.track.closed) return fail('already_closed', 'This track is already closed.');
  if (project.track.nodes.length < 4)
    return fail('needs_points', 'Add more track before finishing to the station.');
  if (project.track.nodes.length + 4 > PHASE_2_NODE_LIMIT)
    return fail('node_limit', 'Not enough point capacity to finish this connection.');
  const nodes = project.track.nodes;
  const first = nodes[0]!;
  const second = nodes[1]!;
  const previous = nodes.at(-2)!;
  const last = nodes.at(-1)!;
  const gapVector = subtract(first.position, last.position);
  const gap = length(gapVector);
  if (gap > 85)
    return fail(
      'too_far',
      `The track exit is ${gap.toFixed(0)} m from the station. Finish works within 85 m.`,
    );
  if (Math.abs(first.position.y - last.position.y) > 35)
    return fail('height_mismatch', 'Bring the track exit within 35 m of station height.');
  const outgoing = normalize(subtract(last.position, previous.position));
  const incoming = normalize(subtract(first.position, second.position));
  const lateral = normalize({ x: -outgoing.z, y: 0, z: outgoing.x });
  const bulge = Math.max(10, Math.min(24, gap * 0.5));
  const buildPositions = (bridge: number, side: number): Vec3[] => {
    const startAnchor = add(last.position, scale(outgoing, bridge));
    const endAnchor = add(first.position, scale(incoming, bridge));
    return [
      startAnchor,
      add(startAnchor, scale(lateral, bulge * side)),
      add(endAnchor, scale(lateral, bulge * side)),
      endAnchor,
    ];
  };
  const bounds = projectPlotBounds(project);
  const withinPlot = (candidate: readonly Vec3[]) =>
    candidate.every(
      (point) =>
        point.x >= bounds.minX &&
        point.x <= bounds.maxX &&
        point.z >= bounds.minZ &&
        point.z <= bounds.maxZ &&
        point.y >= bounds.minY &&
        point.y <= bounds.maxY,
    );
  const bridgeLengths = [Math.max(8, gap * 0.28), Math.max(8, Math.min(14, gap * 0.2))];
  for (const positions of bridgeLengths.flatMap((bridge) => [
    buildPositions(bridge, 1),
    buildPositions(bridge, -1),
  ])) {
    if (!withinPlot(positions)) continue;
    const next = structuredClone(project);
    const addedSectionIds: string[] = [];
    const newNodes = positions.map<TrackNode>((position, index) => ({
      id: newStableId('node'),
      position,
      bankDegrees: index === 0 ? last.bankDegrees : first.bankDegrees,
      rollWindingDegrees: index === 0 ? last.rollWindingDegrees : first.rollWindingDegrees,
    }));
    for (const node of newNodes) {
      const from = next.track.nodes.at(-1)!;
      next.track.nodes.push(node);
      const section = makeOrderedSection(newStableId('section'), from.id, node.id);
      next.track.sections.push(section);
      addedSectionIds.push(section.id);
    }
    next.track.closed = true;
    const closingSection = makeOrderedSection(
      newStableId('section'),
      next.track.nodes.at(-1)!.id,
      first.id,
    );
    next.track.sections.push(closingSection);
    addedSectionIds.push(closingSection.id);
    for (const section of next.track.sections.slice(-2)) {
      section.behavior = {
        kind: 'brake',
        targetSpeedMps: 2.5,
        decelerationMps2: 8,
      };
    }
    if (!addsNewBlocker(project, next))
      return success(
        assignPieceGroup(next, addedSectionIds, 'custom'),
        'Track finished to the station and ready for Ride Check.',
      );
  }
  return fail(
    'not_enough_room',
    'No safe finish fits here. Add a turn or straight to give the connector more room.',
  );
}

export function assessFinishToStation(project: CoasterProject): FinishAssessment {
  const guidance = measureFinishToStation(project);
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: guidance.summary, guidance };
  if (project.track.nodes.length < 4)
    return { ok: false, code: 'needs_points', message: guidance.summary, guidance };
  if (project.track.geometryKind === 'modular_piecewise_bezier') {
    return assessModularFinishToStation(project, guidance);
  }
  if (project.track.nodes.length + 4 > PHASE_2_NODE_LIMIT)
    return {
      ok: false,
      code: 'node_limit',
      message: 'Not enough track capacity to connect to the station.',
      guidance,
    };
  if (guidance.distanceM > 85)
    return { ok: false, code: 'too_far', message: guidance.summary, guidance };
  if (Math.abs(guidance.elevationDeltaM) > 35)
    return { ok: false, code: 'height_mismatch', message: guidance.summary, guidance };

  if (guidance.directJoinEligible) {
    const first = project.track.nodes[0]!;
    const second = project.track.nodes[1]!;
    const last = project.track.nodes.at(-1)!;
    const stationEntry = normalize(subtract(first.position, second.position));
    const alignedPosition = subtract(first.position, scale(stationEntry, 8));
    const displacement = length(subtract(alignedPosition, last.position));
    if (displacement <= 8) {
      const aligned = structuredClone(project);
      aligned.track.nodes.at(-1)!.position = alignedPosition;
      aligned.track.closed = true;
      const closingSection = makeOrderedSection(
        newStableId('section'),
        aligned.track.nodes.at(-1)!.id,
        first.id,
        {
          kind: 'brake',
          targetSpeedMps: 2.5,
          decelerationMps2: 8,
        },
      );
      aligned.track.sections.push(closingSection);
      if (!addsNewBlocker(project, aligned))
        return {
          ok: true,
          project: touch(assignPieceGroup(aligned, [closingSection.id], 'custom')),
          mode: 'aligned',
          message: 'Final handle aligned and track finished to the station.',
          guidance,
        };
    }
    const direct = connectEndpoints(project);
    if (direct.ok)
      return {
        ok: true,
        project: direct.project,
        mode: 'direct',
        message: direct.message,
        guidance,
      };
  }

  const generated = finishConnection(project);
  if (generated.ok)
    return {
      ok: true,
      project: generated.project,
      mode: 'generated',
      message: generated.message,
      guidance,
    };
  return {
    ok: false,
    code: generated.code as FinishFailureCode,
    message: generated.message,
    guidance,
  };
}

export function renameProject(project: CoasterProject, title: string): TrackOperationResult {
  const trimmed = title.trim().slice(0, 80);
  if (!trimmed) return fail('empty_title', 'Enter a coaster name.');
  const next = structuredClone(project);
  next.metadata.title = trimmed;
  return success(next, 'Coaster renamed.');
}
